<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="720" scrollable persistent>
    <v-card class="eqc-card" v-if="value">
      <!-- Header -->
      <div class="eqc-header">
        <div class="d-flex align-center">
          <v-avatar size="40" class="mr-3 eqc-avatar">
            <v-icon color="white" size="22">mdi-file-certificate-outline</v-icon>
          </v-avatar>
          <div>
            <div class="eqc-title">Upload as Evidence</div>
            <div class="eqc-subtitle">Attach this report to your compliance documentation</div>
          </div>
        </div>
        <v-btn icon dark @click="close" :disabled="submitting">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="eqc-body">
        <!-- Attached file chip -->
        <div class="eqc-file-chip">
          <v-icon color="#00695c" class="mr-2">mdi-paperclip</v-icon>
          <div class="eqc-file-info">
            <div class="eqc-file-name">{{ file ? file.name : '' }}</div>
            <div class="eqc-file-size" v-if="file">{{ formatFileSize(file.size) }}</div>
          </div>
          <v-chip x-small color="#e0f2f1" text-color="#00695c" class="ml-auto">
            <v-icon x-small left>mdi-check-circle</v-icon>
            Pre-attached
          </v-chip>
        </div>

        <v-form ref="form" v-model="valid" class="mt-4">
          <v-text-field
            v-model="form.title"
            label="Name"
            outlined
            dense
            :rules="[rules.required, rules.duplicateName]"
            :error-messages="duplicateNameExists ? 'Evidence with this name already exists' : ''"
            class="mb-1"
            @input="debouncedCheckDuplicate"
          ></v-text-field>

          <v-textarea
            v-model="form.description"
            label="Description"
            outlined
            dense
            auto-grow
            rows="2"
            max-rows="5"
            :rules="[rules.required]"
            class="mb-1"
          ></v-textarea>

          <v-row>
            <v-col cols="12" sm="6">
              <v-combobox
                v-model="form.type"
                :items="typeItems"
                label="Type"
                outlined
                dense
                clearable
                :rules="[rules.required]"
              ></v-combobox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.source"
                :items="['Manual', 'Threat Spider', 'Dependency Track', 'Threat Dragon JSON']"
                label="Source"
                outlined
                dense
                :rules="[rules.required]"
              ></v-select>
            </v-col>
          </v-row>

          <v-autocomplete
            v-model="selectedSocIds"
            :items="socList"
            item-text="name"
            item-value="id"
            label="Subject(s) of Certification (optional)"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            class="mb-1"
          ></v-autocomplete>

          <v-expansion-panels flat class="eqc-advanced">
            <v-expansion-panel>
              <v-expansion-panel-header class="eqc-advanced-header">
                <span
                  ><v-icon small color="primary" class="mr-1">mdi-tune</v-icon>Advanced quality
                  indicators (optional)</span
                >
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" sm="6">
                    <div class="eqc-slider-label">
                      <v-icon x-small color="amber darken-2" class="mr-1">mdi-star</v-icon>Quality
                      <span class="eqc-slider-value">{{
                        form.quality != null ? form.quality + '%' : '—'
                      }}</span>
                    </div>
                    <v-slider
                      v-model="form.quality"
                      min="0"
                      max="100"
                      step="1"
                      thumb-label
                      hide-details
                    ></v-slider>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="eqc-slider-label">
                      <v-icon x-small color="blue darken-2" class="mr-1">mdi-source-branch</v-icon
                      >Provenance
                      <span class="eqc-slider-value">{{
                        form.provenance != null ? form.provenance + '%' : '—'
                      }}</span>
                    </div>
                    <v-slider
                      v-model="form.provenance"
                      min="0"
                      max="100"
                      step="1"
                      thumb-label
                      hide-details
                    ></v-slider>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="eqc-slider-label">
                      <v-icon x-small color="deep-orange darken-1" class="mr-1"
                        >mdi-help-circle</v-icon
                      >Uncertainty
                      <span class="eqc-slider-value">{{
                        form.uncertainty != null ? form.uncertainty + '%' : '—'
                      }}</span>
                    </div>
                    <v-slider
                      v-model="form.uncertainty"
                      min="0"
                      max="100"
                      step="1"
                      thumb-label
                      hide-details
                    ></v-slider>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="eqc-slider-label">
                      <v-icon x-small color="purple darken-1" class="mr-1"
                        >mdi-thought-bubble</v-icon
                      >Assumption
                      <span class="eqc-slider-value">{{
                        form.assumption != null ? form.assumption + '%' : '—'
                      }}</span>
                    </div>
                    <v-slider
                      v-model="form.assumption"
                      min="0"
                      max="100"
                      step="1"
                      thumb-label
                      hide-details
                    ></v-slider>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-form>
      </v-card-text>

      <v-card-actions class="eqc-actions">
        <v-spacer></v-spacer>
        <v-btn text @click="close" :disabled="submitting">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="submitting"
          :disabled="!valid || duplicateNameExists"
          @click="submit"
        >
          <v-icon left small>mdi-upload</v-icon>
          Create Evidence
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import evidenceApi from '../api/evidenceApi';
import Soc from '@/modules/soc/api/soc.js';
import EventBus from '@/helpers/event-bus.js';
import { debounce } from 'lodash';

export default {
  name: 'EvidenceQuickCreateDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    file: {
      type: File,
      default: null,
    },
    defaultTitle: {
      type: String,
      default: '',
    },
    defaultType: {
      type: String,
      default: 'Test Results',
    },
  },
  data() {
    return {
      valid: false,
      submitting: false,
      duplicateNameExists: false,
      form: {
        title: '',
        description: '',
        type: '',
        source: 'Manual',
        quality: null,
        provenance: null,
        uncertainty: null,
        assumption: null,
      },
      selectedSocIds: [],
      socList: [],
      rules: {
        required: v => !!v || 'Required.',
        duplicateName: () => !this.duplicateNameExists || 'Evidence with this name already exists.',
      },
    };
  },
  computed: {
    typeItems() {
      return [
        this.$tc('evidence.type.screenshots'),
        this.$tc('evidence.type.logs'),
        this.$tc('evidence.type.policyDocuments'),
        this.$tc('evidence.type.procedureManuals'),
        this.$tc('evidence.type.restResults'),
      ];
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.resetForm();
        this.fetchSocs();
      }
    },
  },
  created() {
    this.debouncedCheckDuplicate = debounce(this.checkDuplicateName, 400);
  },
  methods: {
    resetForm() {
      this.form = {
        title: this.defaultTitle || '',
        description: '',
        type: this.defaultType || '',
        source: 'Manual',
        quality: null,
        provenance: null,
        uncertainty: null,
        assumption: null,
      };
      this.selectedSocIds = [];
      this.duplicateNameExists = false;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    async checkDuplicateName() {
      if (!this.form.title || this.form.title.trim() === '') {
        this.duplicateNameExists = false;
        return;
      }
      try {
        const response = await evidenceApi.checkDuplicateName(this.form.title);
        this.duplicateNameExists = response.data;
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.validate();
        });
      } catch (error) {
        console.error('Error checking duplicate name:', error);
        this.duplicateNameExists = false;
      }
    },
    async fetchSocs() {
      try {
        const response = await Soc.all();
        this.socList = response.data || [];
      } catch (error) {
        console.error('Error fetching SoCs:', error);
      }
    },
    close() {
      this.$emit('input', false);
    },
    async submit() {
      if (!this.$refs.form.validate() || this.duplicateNameExists) return;

      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('fileName', this.file.name);
      formData.append('title', this.form.title);
      formData.append('description', this.form.description);
      formData.append('type', this.form.type);
      formData.append('source', this.form.source || 'Manual');
      formData.append('version', '1.0');
      formData.append('uncertainty', this.form.uncertainty != null ? this.form.uncertainty : '');
      formData.append('quality', this.form.quality != null ? this.form.quality : '');
      formData.append('provenance', this.form.provenance != null ? this.form.provenance : '');
      formData.append('assumption', this.form.assumption != null ? this.form.assumption : '');
      if (this.selectedSocIds && this.selectedSocIds.length > 0) {
        formData.append('socIds', this.selectedSocIds.join(','));
      }

      this.submitting = true;
      try {
        await evidenceApi.create(formData);
        EventBus.$emit('snackbar', {
          text: `Evidence "${this.form.title}" created successfully`,
          color: 'green',
        });
        this.$emit('created', { title: this.form.title, fileName: this.file.name });
        this.close();
      } catch (error) {
        console.error('Error creating evidence:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.eqc-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.eqc-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.eqc-avatar {
  background: rgba(255, 255, 255, 0.2) !important;
}

.eqc-title {
  color: white;
  font-size: 1.15rem;
  font-weight: 700;
}

.eqc-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8rem;
}

.eqc-body {
  padding: 20px !important;
}

.eqc-file-chip {
  display: flex;
  align-items: center;
  background: #f1f8f6;
  border: 1px solid #b2dfdb;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 4px;
}

.eqc-file-info {
  min-width: 0;
}

.eqc-file-name {
  font-weight: 600;
  font-size: 0.88rem;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 340px;
}

.eqc-file-size {
  font-size: 0.72rem;
  color: #78909c;
}

.eqc-advanced {
  background: transparent !important;
}

.eqc-advanced-header {
  font-size: 0.85rem;
  font-weight: 600;
  color: #37474f;
  min-height: 40px !important;
  padding: 0 8px !important;
}

.eqc-slider-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #546e7a;
  display: flex;
  align-items: center;
}

.eqc-slider-value {
  margin-left: auto;
  font-weight: 700;
  color: #1a237e;
}

.eqc-actions {
  padding: 14px 20px !important;
  border-top: 1px solid #f0f0f0;
}
</style>
